import { brandStorage as localStorage } from '@/utils/brandSession';
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from './Layout';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { 
  BarChart3, Download, CheckCircle, Clock, AlertTriangle,
  Building, Calendar, Users, Globe, RefreshCw, Send, FileText,
  ChevronDown, Filter, Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../utils/config';


export default function TourismFlowsManager() {
  const [stats, setStats] = useState(null);
  const [pendingReports, setPendingReports] = useState([]);
  const [properties, setProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [selectedReport, setSelectedReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [month, setMonth] = useState(new Date().getMonth() || 12);
  const [year, setYear] = useState(new Date().getMonth() === 0 ? new Date().getFullYear() - 1 : new Date().getFullYear());
  const [propertySearch, setPropertySearch] = useState(''); // Campo ricerca immobili

  // XML Generator state
  const [xmlGeneratorOpen, setXmlGeneratorOpen] = useState(false);
  const [liguriaProvinces, setLiguriaProvinces] = useState([]);
  const [selectedProvince, setSelectedProvince] = useState('');
  const [provinceProperties, setProvinceProperties] = useState([]);
  const [selectedPropertyCode, setSelectedPropertyCode] = useState('');
  const [xmlDateRange, setXmlDateRange] = useState({
    start: `${year}-${String(month).padStart(2, '0')}-01`,
    end: `${year}-${String(month).padStart(2, '0')}-${new Date(year, month, 0).getDate()}`
  });
  const [generatingXml, setGeneratingXml] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('hw_token');
      
      // Fetch stats and pending reports in parallel
      const [statsRes, pendingRes, propsRes] = await Promise.all([
        axios.get(`${API}/api/tourism-flows/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/api/tourism-flows/pending`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/api/properties`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setStats(statsRes.data);
      setPendingReports(pendingRes.data.pending_reports || []);
      setProperties(propsRes.data || []);
    } catch (err) {
      console.error('Error fetching data:', err);
      toast.error('Errore nel caricamento dati');
    } finally {
      setLoading(false);
    }
  };

  // Fetch Liguria provinces for XML generator
  const fetchLiguriaProvinces = async () => {
    try {
      const token = localStorage.getItem('hw_token');
      const response = await axios.get(
        `${API}/api/tourism/liguria-provinces`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setLiguriaProvinces(response.data.provinces || []);
    } catch (err) {
      console.error('Error fetching provinces:', err);
    }
  };

  // Fetch properties for selected province
  const fetchProvinceProperties = async (provinceName) => {
    if (!provinceName) {
      setProvinceProperties([]);
      return;
    }
    try {
      const token = localStorage.getItem('hw_token');
      const province = liguriaProvinces.find(p => p.name === provinceName);
      if (!province) return;
      
      // Fetch ALL properties (not just the ones loaded in the main list)
      const response = await axios.get(
        `${API}/api/properties`,
        { 
          headers: { Authorization: `Bearer ${token}` },
          params: { limit: 2000 }  // Get all properties
        }
      );
      
      const allProperties = Array.isArray(response.data) ? response.data : (response.data.properties || []);
      
      // Filter by province CIR prefix
      const filtered = allProperties.filter(p => 
        p.cir && p.cir.startsWith(province.code)
      );
      filtered.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      setProvinceProperties(filtered);
    } catch (err) {
      console.error('Error filtering province properties:', err);
    }
  };

  // Generate and download ROSS1000 XML
  const generateRoss1000Xml = async () => {
    if (!selectedProvince) {
      toast.error('Seleziona una provincia');
      return;
    }

    try {
      setGeneratingXml(true);
      const token = localStorage.getItem('hw_token');
      
      const requestBody = {
        province: selectedProvince,
        data_inizio: xmlDateRange.start,
        data_fine: xmlDateRange.end
      };
      
      if (selectedPropertyCode) {
        requestBody.property_code = selectedPropertyCode;
      }
      
      const response = await axios.post(
        `${API}/api/tourism/generate-ross1000-xml`,
        requestBody,
        { 
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );

      const blob = new Blob([response.data], { type: 'application/xml' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      let filename = `ROSS1000_${selectedProvince}_${xmlDateRange.start}_${xmlDateRange.end}.xml`;
      if (selectedPropertyCode) {
        filename = `ROSS1000_${selectedProvince}_${selectedPropertyCode}_${xmlDateRange.start}_${xmlDateRange.end}.xml`;
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success(`File XML generato: ${filename}`);
      setXmlGeneratorOpen(false);
    } catch (err) {
      console.error('Error generating XML:', err);
      toast.error(err.response?.data?.detail || 'Errore nella generazione XML');
    } finally {
      setGeneratingXml(false);
    }
  };

  const generateReport = async (propertyId) => {
    try {
      const token = localStorage.getItem('hw_token');
      const response = await axios.get(
        `${API}/api/tourism-flows/report/${propertyId}?month=${month}&year=${year}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSelectedReport(response.data);
      toast.success('Report generato!');
    } catch (err) {
      console.error('Error generating report:', err);
      toast.error('Errore nella generazione del report');
    }
  };

  const downloadCSV = async (propertyId, detailed = false) => {
    try {
      const token = localStorage.getItem('hw_token');
      const response = await axios.get(
        `${API}/api/tourism-flows/export/csv/${propertyId}?month=${month}&year=${year}&detailed=${detailed}`,
        { 
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `flussi_${propertyId}_${year}_${month}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('CSV scaricato!');
    } catch (err) {
      console.error('Error downloading CSV:', err);
      toast.error('Errore nel download');
    }
  };

  const markAsSubmitted = async (propertyId) => {
    try {
      const token = localStorage.getItem('hw_token');
      await axios.post(
        `${API}/api/tourism-flows/report/${propertyId}/submit?month=${month}&year=${year}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Report segnato come inviato!');
      fetchData();
    } catch (err) {
      console.error('Error marking as submitted:', err);
      toast.error('Errore');
    }
  };

  const monthNames = [
    'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
    'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'
  ];

  return (
    <Layout>
    <div className="p-6 space-y-6" data-testid="tourism-flows-manager">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Flussi Turistici - ROSS1000</h1>
          <p className="text-gray-600">Gestione comunicazioni ISTAT per gli osservatori regionali</p>
        </div>
        <div className="flex gap-2">
          <Button 
            onClick={() => {
              fetchLiguriaProvinces();
              setXmlDateRange({
                start: `${year}-${String(month).padStart(2, '0')}-01`,
                end: `${year}-${String(month).padStart(2, '0')}-${new Date(year, month, 0).getDate()}`
              });
              setXmlGeneratorOpen(true);
            }} 
            variant="outline"
            className="border-green-500 text-green-700 hover:bg-green-50"
            data-testid="generate-xml-btn"
          >
            <Download className="h-4 w-4 mr-2" />
            Genera XML Liguria
          </Button>
          <Button onClick={fetchData} variant="outline" disabled={loading} data-testid="refresh-btn">
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Aggiorna
          </Button>
        </div>
      </div>

      {/* XML Generator Dialog */}
      <Dialog open={xmlGeneratorOpen} onOpenChange={setXmlGeneratorOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-green-600" />
              Genera XML ROSS1000 - Liguria
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <p className="text-sm text-gray-600">
              Genera il file XML per l'upload manuale sui portali ROSS1000 della Liguria.
            </p>

            {/* Province Selection */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Provincia</label>
              <select
                value={selectedProvince}
                onChange={(e) => {
                  setSelectedProvince(e.target.value);
                  setSelectedPropertyCode('');
                  fetchProvinceProperties(e.target.value);
                }}
                className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-green-500"
                data-testid="province-select"
              >
                <option value="">-- Seleziona Provincia --</option>
                {liguriaProvinces.map((prov) => (
                  <option key={prov.code} value={prov.name}>
                    {prov.name} ({prov.properties_count} immobili)
                  </option>
                ))}
              </select>
            </div>

            {/* Property Selection (optional) */}
            {selectedProvince && (
              <div className="space-y-2">
                <label className="text-sm font-medium">
                  Immobile <span className="text-gray-400 font-normal">(opzionale)</span>
                </label>
                <select
                  value={selectedPropertyCode}
                  onChange={(e) => setSelectedPropertyCode(e.target.value)}
                  className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-green-500"
                  data-testid="property-select"
                >
                  <option value="">-- Tutti gli immobili --</option>
                  {provinceProperties.map((prop) => (
                    <option key={prop.property_code} value={prop.property_code}>
                      {prop.property_code} - {prop.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Date Range */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Data Inizio</label>
                <Input
                  type="date"
                  value={xmlDateRange.start}
                  onChange={(e) => setXmlDateRange(prev => ({ ...prev, start: e.target.value }))}
                  data-testid="date-start"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Data Fine</label>
                <Input
                  type="date"
                  value={xmlDateRange.end}
                  onChange={(e) => setXmlDateRange(prev => ({ ...prev, end: e.target.value }))}
                  data-testid="date-end"
                />
              </div>
            </div>

            {/* Info Box */}
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-xs text-blue-800">
                <strong>Nota:</strong> {selectedPropertyCode 
                  ? `File per l'immobile ${selectedPropertyCode}`
                  : 'File per tutti gli immobili della provincia'
                }
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setXmlGeneratorOpen(false)}>
              Annulla
            </Button>
            <Button 
              onClick={generateRoss1000Xml}
              disabled={generatingXml || !selectedProvince}
              className="bg-green-600 hover:bg-green-700"
              data-testid="download-xml-btn"
            >
              {generatingXml ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Generazione...
                </>
              ) : (
                <>
                  <Download className="h-4 w-4 mr-2" />
                  Scarica XML
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Period Selector */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-gray-500" />
              <span className="font-medium">Periodo:</span>
            </div>
            <select 
              value={month} 
              onChange={(e) => setMonth(parseInt(e.target.value))}
              className="border rounded px-3 py-2"
              data-testid="month-select"
            >
              {monthNames.map((name, i) => (
                <option key={i} value={i + 1}>{name}</option>
              ))}
            </select>
            <select 
              value={year} 
              onChange={(e) => setYear(parseInt(e.target.value))}
              className="border rounded px-3 py-2"
              data-testid="year-select"
            >
              {[2024, 2025, 2026].map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
            <Badge variant="outline" className="ml-2">
              Scadenza: 5 {monthNames[month % 12]} {month === 12 ? year + 1 : year}
            </Badge>
          </div>
        </CardContent>
      </Card>

      {/* Stats Overview */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Building className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500">Proprietà Abilitate</p>
                <p className="text-xl font-bold">{stats.properties_enabled}</p>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-green-200 bg-green-50/30">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500">Report Inviati</p>
                <p className="text-xl font-bold text-green-700">{stats.reports_submitted}</p>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Users className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500">Arrivi Totali</p>
                <p className="text-xl font-bold">{stats.totals?.arrivals || 0}</p>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <Globe className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500">Presenze Totali</p>
                <p className="text-xl font-bold">{stats.totals?.presences || 0}</p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Properties List */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building className="h-5 w-5" />
                Proprietà
                <Badge variant="outline">{properties.length}</Badge>
              </CardTitle>
              {/* Campo ricerca immobili */}
              <div className="mt-2">
                <Input
                  placeholder="Cerca immobile per nome o codice..."
                  value={propertySearch}
                  onChange={(e) => setPropertySearch(e.target.value)}
                  className="w-full"
                  data-testid="property-search-input"
                />
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-[500px] overflow-y-auto">
                {properties
                  .filter(p => {
                    if (!propertySearch) return true;
                    const search = propertySearch.toLowerCase();
                    return (
                      p.name?.toLowerCase().includes(search) ||
                      p.property_code?.toLowerCase().includes(search) ||
                      p.cir?.toLowerCase().includes(search) ||
                      p.city?.toLowerCase().includes(search)
                    );
                  })
                  .map((property) => (
                  <div
                    key={property.id}
                    className={`p-3 rounded-lg border cursor-pointer transition-all hover:border-blue-300 ${
                      selectedProperty?.id === property.id ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
                    }`}
                    onClick={() => {
                      setSelectedProperty(property);
                      generateReport(property.id);
                    }}
                    data-testid={`property-row-${property.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{property.name}</span>
                          <Badge variant="outline" className="text-xs">
                            {property.property_code}
                          </Badge>
                        </div>
                        <div className="text-sm text-gray-500">
                          {property.city}, {property.region}
                        </div>
                        {property.cir && (
                          <div className="text-xs text-gray-400 mt-1">
                            CIR: {property.cir}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={(e) => {
                            e.stopPropagation();
                            downloadCSV(property.id);
                          }}
                          data-testid={`download-csv-${property.id}`}
                        >
                          <Download className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Report Preview */}
        <div>
          <Card className="sticky top-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="h-5 w-5" />
                Report {monthNames[month - 1]} {year}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {selectedReport ? (
                <div className="space-y-4">
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="font-medium">{selectedReport.report?.property_name}</p>
                    <p className="text-sm text-gray-500">{selectedReport.report?.region}</p>
                    {selectedReport.report?.cir && (
                      <p className="text-xs text-gray-400">CIR: {selectedReport.report.cir}</p>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="p-3 bg-blue-50 rounded-lg text-center">
                      <p className="text-2xl font-bold text-blue-700">
                        {selectedReport.summary?.total_arrivals || 0}
                      </p>
                      <p className="text-xs text-blue-600">Arrivi</p>
                    </div>
                    <div className="p-3 bg-purple-50 rounded-lg text-center">
                      <p className="text-2xl font-bold text-purple-700">
                        {selectedReport.summary?.total_presences || 0}
                      </p>
                      <p className="text-xs text-purple-600">Presenze</p>
                    </div>
                  </div>

                  <div className="border-t pt-3">
                    <p className="text-sm font-medium mb-2">Breakdown</p>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-500">Italiani</span>
                        <span>{selectedReport.summary?.italians?.arrivals || 0} arrivi / {selectedReport.summary?.italians?.presences || 0} notti</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Stranieri</span>
                        <span>{selectedReport.summary?.foreigners?.arrivals || 0} arrivi / {selectedReport.summary?.foreigners?.presences || 0} notti</span>
                      </div>
                    </div>
                  </div>

                  <div className="border-t pt-3">
                    <p className="text-sm text-gray-500 mb-2">
                      Scadenza invio: <strong>{selectedReport.summary?.deadline}</strong>
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Button 
                      className="w-full" 
                      variant="outline"
                      onClick={() => downloadCSV(selectedProperty?.id)}
                      data-testid="download-csv-btn"
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Scarica CSV ROSS1000
                    </Button>
                    <Button 
                      className="w-full" 
                      variant="outline"
                      onClick={() => downloadCSV(selectedProperty?.id, true)}
                      data-testid="download-detailed-btn"
                    >
                      <FileText className="h-4 w-4 mr-2" />
                      Scarica Dettagliato
                    </Button>
                    <Button 
                      className="w-full bg-green-600 hover:bg-green-700"
                      onClick={() => markAsSubmitted(selectedProperty?.id)}
                      data-testid="mark-submitted-btn"
                    >
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Segna come Inviato
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <BarChart3 className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                  <p>Seleziona una proprietà per vedere il report</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Pending Reports Alert */}
      {pendingReports.length > 0 && (
        <Card className="border-orange-200 bg-orange-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-orange-700">
              <AlertTriangle className="h-5 w-5" />
              Report Pendenti ({pendingReports.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {pendingReports.map((report, i) => (
                <div key={i} className="flex items-center justify-between p-2 bg-white rounded border">
                  <div>
                    <span className="font-medium">{report.property_name}</span>
                    <span className="text-sm text-gray-500 ml-2">
                      {monthNames[report.month - 1]} {report.year}
                    </span>
                  </div>
                  <Badge variant="outline" className="text-orange-600">
                    Scadenza: {report.deadline}
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Info Box */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Globe className="h-5 w-5 text-blue-600 mt-0.5" />
            <div className="text-sm text-blue-800">
              <p className="font-medium mb-1">Informazioni ROSS1000</p>
              <ul className="list-disc list-inside space-y-1 text-blue-700">
                <li>I dati vanno inviati entro il <strong>5 del mese successivo</strong></li>
                <li>ROSS1000 è il sistema nazionale che sostituisce i vecchi portali regionali</li>
                <li>I dati richiesti sono: arrivi, presenze, provenienza (aggregata)</li>
                <li>Non vengono inviati dati anagrafici personali (GDPR)</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
    </Layout>
  );
}
